import get from 'lodash/get'
import { mapState } from 'pinia'
import { reduceAnswerList, changeAnswer, dependencySatisfied  } from '~/libs/question-helpers';
import { useQuestionsStore } from '~/store/questions';
import { useUserStore } from '~/store/user'

/**
 * The form mixin is intended to create a streamlined experience of implementing
 */
export default {
  emits: ['isInvalid'],
  data() {
    return {
      /**
       * This is the underlying data point that determines the fields in a form for the default computed fieldList
       */
      formFieldList: [],

      /**
       * This is the internal array of answers that the borrower has provided in this form. This is live state, not
       * persisted values
       */
      formAnswers: [],

      /**
       * This array stores the invalidity of fields as they are changed
       */
      invalidity: [],

      /**
       * Determines whether or not to hide previous answers (by default functionality of `freshForm` computed
       * field)
       */
      fresh: false,

      /**
       * This array makes specific aliases always display as 'fresh'
       */
      freshAnswerAliases: [],
    }
  },

  async mounted() {
    await this.formMixinMounted()
  },

  computed: {
    ...mapState(useQuestionsStore, ['componentQuestions', 'keyedQuestions', 'currentAnswers']),

    /**
     * The fieldList computed field can be overridden by the inheriting component, but it should return
     * an array of field aliases. Aliases also allow question type overrides with an `@{type}` suffix
     *
     * Refer to <DOCUMENTATION> for examples of how this computed field can be leveraged
     * @returns {string[]}
     */
    fieldList() {
      return this.formFieldList
    },

    /**
     * This returns the form's live-state answers based on the formAnswers array
     * @returns {{[alias]: value}} answer object
     */
    unsavedAnswers() {
      return this.formAnswers.reduce(reduceAnswerList, {})
    },

    /**
     * This returns the combined answer object of persisted answers (saved in DB and store) and
     * the form's live-state answers. Leveraged when the form is not "fresh" and should show
     * previous responses
     * @returns {{[alias]: value}} answer object
     */
    currentFormAnswers() {
      return { ...this.currentAnswers, ...this.unsavedAnswers }
    },

    /**
     * Returns a boolean of whether or not to hide previous responses to this form. Hide the answers
     * if the return value is true
     * @returns {Boolean}
     */
    freshForm() {
      return Boolean(this.fresh)
    },


    /**
     * Creates the equivalent to "componentQuestions" using this form's state answers to populate the
     * value field in each question
     * @returns {ApplicationFieldQuestion[]}
     */
     allFormQuestions() {
      return this.fieldList.map((key) => {
        let [alias, type] = key.split('@'),  component = this.keyedQuestions[alias]
        if (get(component, 'fields.length') == 1 && typeof component.fields[0] == 'string') {
          alias = component.fields[0]
        }

        const _getAliasValue = (_alias) => {
          if (this.freshForm || this.freshAnswerAliases.includes(_alias)) {
            return this.unsavedAnswers[_alias]
          }
          return this.currentFormAnswers[_alias]
        }

        return !component ? null : {
          ...component,
          type: type || component.type,
          value: _getAliasValue(alias),
          components: !component.components ? undefined : component.components.map(c => ({
              ...c,
              value: _getAliasValue(c.alias)
            })
          )
        }
      })
      .filter(Boolean)
    },

    /**
     * Filters the allFormQuestions computed field response to those questions that are displayable according to
     * dependency logic. Favors the state answers over the persisted answers
     * @returns {ApplicationFieldQuestion[]}
     */
    formQuestions() {
      return this.allFormQuestions.filter(q => dependencySatisfied(q, this.lookup))
    },

    /**
     * Returns the ApplicationFieldQuestions, favoring the state values over persisted answers,
     * also returns the component values for their aliases
     * @returns {{[alias]: ApplicationFieldQuestion}}
     */
    lookup() {
      return {
          ...this.componentQuestions,
          ...this.allFormQuestions.flatMap(q => q.components ? q.components.concat([q]) : [q])
            .reduce((carry, q) => (carry[q.alias] = q, carry), {})
        }
    },

    /**
     * Checks for any visible questions that have reported they are invalid, if so it returns true
     * @returns {Boolean} isInvalid
     */
    isInvalid() {
      if (!process.client) return true
      const filteredInvalidity = this.invalidity.filter(({alias}) => {
          return this.formQuestions.find(({alias: qAlias}) => qAlias == alias)
        })
      return filteredInvalidity.some(({invalid}) => invalid)
    },
  },
  methods: {
    async formMixinMounted() {
      await useQuestionsStore(this.$pinia).getQuestions({aliases: this.fieldList})
    },

    /**
     * Manages the validity of a form. Emits whether the form is invalid after updating the invalidity object
     * @param {string} key
     * @param {boolean} isInvalid
     */
    updateValidity(key, isInvalid) {
      if (!key) return // return if the key is undefined
      const questionInvalid = {alias: key, invalid: isInvalid}
      const index = this.invalidity.findIndex(({alias}) => key == alias)
      if (index < 0) {
        this.invalidity.push(questionInvalid)
      } else {
        this.invalidity.splice(index, 1, questionInvalid)
      }
      this.$emit('isInvalid', this.isInvalid)
    },

    /**
     * Updates the state answers with the parameter value assuming it's not invalid
     * @param {*} answer
     */
    updateAnswer({ attr: {alias, value} = {}, isInvalid } = {}) {
      if (!isInvalid && alias) {
        changeAnswer(this.formAnswers, { alias, value })
        useQuestionsStore(this.$pinia).updateAnswer({ alias, value })
        const piniaUserStore = useUserStore(this.$pinia)
        piniaUserStore.reauth()
      }
    },

    /**
     * Clears the state answers on the form, replacing them with the persisted values (if not fresh)
     */
    clearAnswers() {
      this.formAnswers = []
    },
  },
}
